import { Divider } from '@ds';
import { RoutePlannerModal } from '@lp/features/RoutePlanner/components/RouteModals/components/RoutePlannerModal';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useGetQuoteDetails } from '@lp/features/RoutePlanner/hooks/useGetQuoteDetails';
import { QuoteApproveInternallyBody } from '@/api/apis/quote';
import { QuoteDetails } from './QuoteDetails';
import { getCaseSuffixes, getColorsByOrganType } from '@/utils/helpers';
import { useLogisticsSnapshot } from '@/queries/route-planner';

export type QuoteDetailsModalProps = {
  isOpen: boolean;
  isViewOnly?: boolean;
  recallQuote?: boolean;
  desyncedState?: boolean;
  onApprove: (data: QuoteApproveInternallyBody & { id: string }) => Promise<void>;
  onReject: (caseId: string, comment?: string) => Promise<void>;
  onRecall: (caseId: string, comment: string) => Promise<void>;
  onApproveTxCenterProvider?: (caseId: string, comment?: string) => Promise<void>;
  onCloseModal: () => void;
};

export const QuoteDetailsModal = ({
  isOpen,
  isViewOnly,
  recallQuote,
  desyncedState,
  onApprove,
  onReject,
  onRecall,
  onApproveTxCenterProvider,
  onCloseModal,
}: QuoteDetailsModalProps) => {
  const {
    donorId,
    organType,
    donorType,
    reAllocationSequenceNumber,
    opoRequest,
    isExpeditedCase,
    laterality,
    duplicateCaseNumber,
    quote,
  } = useCaseDetailsWithReviews();
  const quoteDetails = useGetQuoteDetails({ quote });
  const { data: logisticsProjectionSnapshot, isFetched } = useLogisticsSnapshot(
    quoteDetails?.logistics.logisticsId,
    quoteDetails?.logistics.version,
  );
  const caseSuffixes = getCaseSuffixes(
    organType,
    reAllocationSequenceNumber,
    opoRequest,
    isExpeditedCase,
    laterality,
    duplicateCaseNumber,
  );
  const colors = getColorsByOrganType(organType);

  const ModalHeader = (
    <div className="flex flex-row items-center gap-2 pt-[9px]">
      <h1 className={`${colors.text50} text-hl`}>{donorType}</h1>
      <Divider className="h-6" orientation="vertical" />
      <div className="flex items-center gap-1 font-mono text-3xl text-base-5">
        <span>{donorId}</span>
        {caseSuffixes.split(' ').map((suffix) => (
          <span key={suffix} className={colors.textDefault}>
            {suffix}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <RoutePlannerModal size="2xl" title={ModalHeader} isOpen={isOpen} isLoading={!isFetched} onClose={onCloseModal}>
      {isFetched && quoteDetails && (
        <QuoteDetails
          quoteDetails={quoteDetails}
          isViewOnly={isViewOnly}
          desyncedState={desyncedState}
          onApprove={onApprove}
          onReject={onReject}
          onRecall={onRecall}
          onApproveTxCenterProvider={onApproveTxCenterProvider}
          onCloseModal={onCloseModal}
          recallQuote={recallQuote}
          logisticsProjectionSnapshot={logisticsProjectionSnapshot}
        />
      )}
    </RoutePlannerModal>
  );
};
